import React, { useEffect, useRef, useState } from '../lib/teact/teact';
import { getActions } from '../global';

import { dropEnclaveSessionHold, holdEnclaveSession } from '../global/helpers/enclave';
import { callApiWithThrow } from '../api';
import useLang from './useLang';
import useLastCallback from './useLastCallback';

import Modal from '../components/ui/Modal';
import ModalHeader from '../components/ui/ModalHeader';
import PasswordForm from '../components/ui/PasswordForm';

import styles from '../components/settings/SettingsMetadata.module.scss';

export class MetadataAuthorizationCancelled extends Error {}

type PendingAuthorization = {
  accountId: string;
  scope: string;
  execute: () => Promise<void>;
  reject: (error: unknown) => void;
};

export default function useMetadataAuthorization(accountId?: string, scope = accountId ?? '') {
  const lang = useLang();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const pendingRef = useRef<PendingAuthorization>();
  const authorizingRef = useRef<PendingAuthorization>();
  const scopeRef = useRef(scope);
  const mountedRef = useRef(true);
  scopeRef.current = scope;

  const cancel = useLastCallback(() => {
    pendingRef.current?.reject(new MetadataAuthorizationCancelled());
    pendingRef.current = undefined;
    authorizingRef.current = undefined;
    setIsOpen(false);
    setIsLoading(false);
    setError('');
  });

  useEffect(() => {
    cancel();
    return () => {
      pendingRef.current?.reject(new MetadataAuthorizationCancelled());
      pendingRef.current = undefined;
    };
  }, [scope]);
  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  async function runWithAuthorization<T>(operation: () => Promise<T>): Promise<T> {
    const requestedScope = scope;
    try {
      return await operation();
    } catch (cause) {
      if (!(cause instanceof Error) || cause.message !== 'MetadataLocked') throw cause;
      if (!accountId || !mountedRef.current || scopeRef.current !== requestedScope || pendingRef.current) {
        throw new MetadataAuthorizationCancelled();
      }
      return new Promise<T>((resolve, reject) => {
        const request: PendingAuthorization = {
          accountId, scope: requestedScope, reject,
          execute: async () => {
            const result = await operation();
            if (pendingRef.current === request && scopeRef.current === requestedScope) resolve(result);
            else reject(new MetadataAuthorizationCancelled());
          },
        };
        pendingRef.current = request;
        setError('');
        setIsOpen(true);
      });
    }
  }

  const authorize = useLastCallback(async (enclaveToken: string) => {
    const request = pendingRef.current;
    if (!request || authorizingRef.current) return;
    authorizingRef.current = request;
    setIsLoading(true);
    setError('');
    holdEnclaveSession(enclaveToken);
    try {
      await callApiWithThrow('ensureWalletPrepaidAccess', request.accountId, enclaveToken);
      if (pendingRef.current !== request || scopeRef.current !== request.scope) return;
      await request.execute();
      if (pendingRef.current !== request) return;
      pendingRef.current = undefined;
      setIsOpen(false);
    } catch (cause) {
      if (pendingRef.current !== request) return;
      if (cause instanceof Error && cause.message === 'MetadataLocked') {
        setError(lang('Something went wrong.'));
      } else {
        pendingRef.current = undefined;
        setIsOpen(false);
        request.reject(cause);
      }
    } finally {
      if (dropEnclaveSessionHold(enclaveToken)) getActions().releaseEnclaveSession({ enclaveToken });
      if (authorizingRef.current === request) {
        authorizingRef.current = undefined;
        if (mountedRef.current) setIsLoading(false);
      }
    }
  });

  const authorizationModal = isOpen && (
    <Modal
      isOpen
      header={<ModalHeader title={lang('Unlock')} closeClassName={styles.authorizationClose} onClose={cancel} />}
      onClose={cancel}
    >
      <PasswordForm
        isActive
        isLoading={isLoading}
        operationType="passcode"
        submitLabel={lang('Unlock')}
        error={error}
        noAutoConfirm
        onAuthorize={authorize}
        onCancel={cancel}
        onUpdate={() => setError('')}
      />
    </Modal>
  );
  return { runWithAuthorization, authorizationModal };
}
